import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AdminTable from 'common/AdminTable';
import { FaEdit, FaTrash, FaStar, FaRegStar, FaPlus } from 'react-icons/fa';
import './Testimonials.css';

const LIMIT = 10;

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  return `${backendBase}/${normalizedPath}`;
};

const AllTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const buildQuery = (pageNum = 1) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(LIMIT),
    });

    return params.toString();
  };

  const fetchTestimonials = async (pageNum = page) => {
    setLoading(true);
    try {
      const query = buildQuery(pageNum);
      const res = await getRequest(`testimonials?${query}`);

      if (res?.data?.success) {
        setTestimonials(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
        setPage(res.data.page || pageNum);
      } else {
        toast.error(res?.data?.message || 'Failed to load testimonials');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials(1);
  }, []);

  const openEditModal = (item) => {
    setCurrentItem({
      ...item,
      name: item?.name || '',
      city: item?.city || '',
      message: item?.message || '',
      star: Number(item?.star) || 5,
      status: item?.status || 'active',
      newImage: null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (e.target.type === 'file') {
      setCurrentItem((prev) => ({
        ...prev,
        newImage: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }

    setCurrentItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!currentItem) return;

    if (!currentItem.name.trim() || !currentItem.message.trim()) {
      toast.error('Name and message are required');
      return;
    }

    const starInt = parseInt(currentItem.star, 10);
    if (Number.isNaN(starInt) || starInt < 1 || starInt > 5) {
      toast.error('Star must be between 1 and 5');
      return;
    }

    setUpdating(true);

    const payload = new FormData();
    payload.append('name', currentItem.name.trim());
    payload.append('city', currentItem.city?.trim() || '');
    payload.append('message', currentItem.message.trim());
    payload.append('star', String(starInt));
    payload.append('status', currentItem.status || 'active');

    if (currentItem.newImage) {
      payload.append('image', currentItem.newImage);
    }

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await patchRequest(`testimonials/${currentItem.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res?.data?.success) {
        toast.success('Testimonial updated successfully');
        closeModal();
        fetchTestimonials(page);
      } else {
        toast.error(res?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating testimonial');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Delete Testimonial?',
      text: `Are you sure you want to delete testimonial from "${name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          const res = await deleteRequest(`testimonials/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res?.data?.success) {
            toast.success('Testimonial deleted successfully');
            if (testimonials.length === 1 && page > 1) {
              fetchTestimonials(page - 1);
            } else {
              fetchTestimonials(page);
            }
          } else {
            toast.error(res?.data?.message || 'Delete failed');
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || 'Error deleting testimonial');
        }
      }
    });
  };

  const handlePrevPage = () => {
    if (page > 1) fetchTestimonials(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) fetchTestimonials(page + 1);
  };

  const renderStars = (star) => {
    const numericStar = Number(star) || 0;
    const stars = [];

    for (let i = 1; i <= 5; i += 1) {
      stars.push(
        i <= numericStar ? (
          <FaStar key={i} className="star-filled" />
        ) : (
          <FaRegStar key={i} className="star-empty" />
        )
      );
    }

    return <div className="stars">{stars}</div>;
  };

  const tableHeaders = [
    {
      Header: 'S.No',
      accessor: 'sno',
      Cell: ({ row }) => row.index + 1 + (page - 1) * LIMIT,
    },
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) => {
        const imageUrl = getImageUrl(row.original.image);
        return imageUrl ? (
          <img src={imageUrl} alt={row.original.name} className="testimonial-thumb" />
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'City',
      accessor: 'city',
      Cell: ({ row }) => row.original.city || <span className="text-muted">—</span>,
    },
    {
      Header: 'Rating',
      accessor: 'star',
      Cell: ({ row }) => renderStars(row.original.star),
    },
    {
      Header: 'Message',
      accessor: 'message',
      Cell: ({ row }) => {
        const message = row.original.message || '';
        return (
          <div className="msg-wrapper">
            <span className="msg-cell">{message || '—'}</span>
            {message.length > 50 && (
              <button className="msg-more" onClick={() => setViewMessage(row.original)}>
                See more
              </button>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <span className={row.original.status === 'active' ? 'badge-active' : 'badge-inactive'}>
          {row.original.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="d-flex" style={{ gap: 6 }}>
          <button className="btn btn-sm btn-icon edit" onClick={() => openEditModal(row.original)}>
            <FaEdit />
          </button>
          <button
            className="btn btn-sm btn-icon delete"
            onClick={() => handleDelete(row.original.id, row.original.name)}
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center my-4 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h2 className="m-0">Testimonials</h2>
          <small className="text-muted">Manage customer testimonials</small>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/testimonials/add')}>
          <FaPlus /> Add Testimonial
        </button>
      </div>

      <div className="card shadow mb-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-5">Loading testimonials...</div>
          ) : (
            <AdminTable tableHeaders={tableHeaders} tableData={testimonials} />
          )}
        </div>
      </div>

      {testimonials.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: '0.75rem' }}>
          <div className="text-muted">
            Showing {testimonials.length} of {totalItems} testimonials
          </div>
          <div className="d-flex align-items-center">
            <button className="btn btn-secondary mx-1" onClick={handlePrevPage} disabled={page === 1}>
              Previous
            </button>
            <span className="mx-2">Page {page} of {totalPages}</span>
            <button className="btn btn-secondary mx-1" onClick={handleNextPage} disabled={page === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}

      {viewMessage && (
        <div className="modal fade show testimonial-modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Testimonial Message</h5>
                <button type="button" className="close" onClick={() => setViewMessage(null)}>
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="view-message-head">
                  <div>
                    <h6 className="mb-0">{viewMessage.name}</h6>
                    {viewMessage.city && <small className="text-muted">{viewMessage.city}</small>}
                  </div>
                  {renderStars(viewMessage.star)}
                </div>
                <div className="view-message-body">
                  &ldquo;{viewMessage.message}&rdquo;
                </div>
                <div className="mt-3">
                  <span className={viewMessage.status === 'active' ? 'badge-active' : 'badge-inactive'}>
                    {viewMessage.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setViewMessage(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && currentItem && (
        <div className="modal fade show testimonial-modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Testimonial</h5>
                <button type="button" className="close" onClick={closeModal}>
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="editName">Name</label>
                  <input
                    id="editName"
                    name="name"
                    className="form-control"
                    value={currentItem.name}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editCity">City (optional)</label>
                  <input
                    id="editCity"
                    name="city"
                    className="form-control"
                    value={currentItem.city}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editStar">Star (1-5)</label>
                  <select
                    id="editStar"
                    name="star"
                    className="form-control"
                    value={currentItem.star}
                    onChange={handleInputChange}
                  >
                    <option value="1">1 - Poor</option>
                    <option value="2">2 - Fair</option>
                    <option value="3">3 - Good</option>
                    <option value="4">4 - Very Good</option>
                    <option value="5">5 - Excellent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="editStatus">Status</label>
                  <select
                    id="editStatus"
                    name="status"
                    className="form-control"
                    value={currentItem.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="editMessage">Message</label>
                  <textarea
                    id="editMessage"
                    name="message"
                    rows="4"
                    className="form-control"
                    value={currentItem.message}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="editImage">Image (optional)</label>
                  <input
                    id="editImage"
                    name="image"
                    type="file"
                    accept="image/*"
                    className="form-control-file"
                    onChange={handleInputChange}
                  />
                  {currentItem.newImage && (
                    <small className="text-muted d-block mt-1">Selected: {currentItem.newImage.name}</small>
                  )}
                  {!currentItem.newImage && currentItem.image && (
                    <div className="existing-image-preview mt-2">
                      <img src={getImageUrl(currentItem.image)} alt="Current testimonial" />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTestimonials;
